import { debounce } from './debounceThrottle';

export class PlayerCooldown {
  private cooldowns: Map<number, number>;
  private static instance: PlayerCooldown;
  private readonly COOLDOWN_DURATION = 5000; // 5 seconds in milliseconds
  private readonly STORAGE_KEY = 'player_cooldowns';
  private cleanupInterval: number | null = null;

  private constructor() {
    this.cooldowns = new Map();
    this.loadFromStorage();
    this.cleanupExpiredCooldowns();
    
    // Add event listener for storage changes (sync across tabs)
    window.addEventListener('storage', this.handleStorageChange);
  }

  public static getInstance(): PlayerCooldown {
    if (!PlayerCooldown.instance) {
      PlayerCooldown.instance = new PlayerCooldown();
    }
    return PlayerCooldown.instance;
  }

  public setCooldown(playerId: number): void {
    const endTime = Date.now() + this.COOLDOWN_DURATION;
    this.cooldowns.set(playerId, endTime);
    this.saveToStorage();
  }

  public isCooldownActive(playerId: number): boolean {
    const cooldownEnd = this.cooldowns.get(playerId);
    if (!cooldownEnd) return false;
    return Date.now() < cooldownEnd;
  }

  public getCooldownRemaining(playerId: number): number {
    const cooldownEnd = this.cooldowns.get(playerId);
    if (!cooldownEnd) return 0;
    const remaining = cooldownEnd - Date.now();
    return remaining > 0 ? remaining : 0;
  }

  public clearCooldown(playerId: number): void {
    this.cooldowns.delete(playerId);
    this.saveToStorage();
  }

  public getAllActiveCooldowns(): Map<number, number> {
    const now = Date.now();
    const activeCooldowns = new Map<number, number>();
    
    this.cooldowns.forEach((endTime, playerId) => {
      if (endTime > now) {
        activeCooldowns.set(playerId, endTime);
      }
    });
    
    return activeCooldowns;
  }

  private loadFromStorage = (): void => {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        this.cooldowns.clear();
        Object.entries(parsed).forEach(([id, timestamp]) => {
          this.cooldowns.set(Number(id), timestamp as number);
        });
      }
    } catch (error) {
      console.error('Error loading cooldowns from storage:', error);
    }
  }

  private saveToStorage = debounce((): void => {
    try {
      const cooldownsObj = Object.fromEntries(this.cooldowns.entries());
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(cooldownsObj));
    } catch (error) {
      console.error('Error saving cooldowns to storage:', error);
    }
  }, 100);

  private handleStorageChange = (event: StorageEvent): void => {
    if (event.key === this.STORAGE_KEY) {
      this.loadFromStorage();
    }
  }

  private cleanupExpiredCooldowns = (): void => {
    const now = Date.now();
    let hasChanges = false;

    this.cooldowns.forEach((endTime, playerId) => {
      if (now >= endTime) {
        this.cooldowns.delete(playerId);
        hasChanges = true;
      }
    });

    if (hasChanges) {
      this.saveToStorage();
    }

    // Schedule periodic cleanup
    if (this.cleanupInterval !== null) {
      clearTimeout(this.cleanupInterval);
    }
    this.cleanupInterval = window.setTimeout(this.cleanupExpiredCooldowns, 60000); // Run every minute
  }

  public destroy(): void {
    window.removeEventListener('storage', this.handleStorageChange);
    if (this.cleanupInterval !== null) {
      clearTimeout(this.cleanupInterval);
    }
  }
}